/**
 * The staff's own record of a tenant (D39): its kind, a contact and free-text internal notes (a customer reference,
 * the contract or plan). Staff-only by construction: it comes from `/vendor/*` routes, which the tenant's own users
 * cannot call. `vendor.admin` edits the contact and the notes.
 *
 * **Live, and dirty-aware (D35).** The draft is made from a `base` record. When the record changes underneath (the
 * tenant page refreshes on the change channel): with no unsaved edits the draft follows it in place; with unsaved
 * edits a banner offers to keep mine (the next save is made against theirs) or discard mine and load theirs. A save
 * names the `updatedAt` its base was saved at; one that lost to someone else's anyway is answered `conflict` (the
 * container's `conflict`), with the same two choices.
 */
import type { VendorTenantRecordWire } from '@acp/api-contract/vendor.js';
import { type FormEvent, useState } from 'react';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';
import { notesDirty, recordDraftVerdict, tenantKindLabel, tenantKindTone } from '../../lib/vendor.js';

export type TenantRecordEdit = { readonly contact: string; readonly notes: string };

export type TenantRecordCardProps = {
  /** The record as the page last read it. */
  readonly record: VendorTenantRecordWire;
  readonly canEdit: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  /** The last save lost to someone else's (a 409): the page re-reads the record, and the banner offers the choice. */
  readonly conflict: boolean;
  /** Save the draft, made from the record saved at `ifUpdatedAt` (null: never edited). */
  readonly onSave: (edit: TenantRecordEdit, ifUpdatedAt: string | null) => void;
  /** After a conflict: forget it (the choice below is made). */
  readonly onResolve: () => void;
};

export function TenantRecordCard({ record, canEdit, pending, error, conflict, onSave, onResolve }: TenantRecordCardProps) {
  const [base, setBase] = useState(record);
  const [contact, setContact] = useState(record.contact);
  const [notes, setNotes] = useState(record.notes);
  const draft = { contact, notes };
  const dirty = notesDirty(draft, base);
  const verdict = recordDraftVerdict(draft, base, record);
  // Adjusting state to a new record during render (React's documented pattern): see `recordDraftVerdict`.
  if (verdict === 'follow') {
    setBase(record);
    setContact(record.contact);
    setNotes(record.notes);
  }
  const changedUnderneath = verdict === 'offer';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || pending) return;
    onSave({ contact: contact.trim(), notes }, base.updatedAt);
  };
  const keepMine = () => {
    setBase(record);
    onResolve();
  };
  const loadTheirs = () => {
    setBase(record);
    setContact(record.contact);
    setNotes(record.notes);
    onResolve();
  };
  const offer = conflict || changedUnderneath;
  return (
    <Card data-testid="tenant-record">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Staff notes
          <Badge variant={tenantKindTone(record.kind)} data-testid="tenant-kind">
            {tenantKindLabel(record.kind)}
          </Badge>
        </CardTitle>
        <CardDescription>
          Internal to ACP staff: never shown to the tenant’s own users.
          {record.updatedAt === null ? ' Not edited yet.' : ` Last saved ${record.updatedAt}${record.updatedBy === null ? '' : ` by ${record.updatedBy}`}.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={submit}>
          <Field>
            <Label htmlFor="tenant-contact">Contact</Label>
            <Input id="tenant-contact" value={contact} onChange={(e) => setContact(e.target.value)} disabled={!canEdit || pending} maxLength={500} />
          </Field>
          <Field>
            <Label htmlFor="tenant-notes">Notes</Label>
            <Textarea id="tenant-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit || pending} maxLength={5000} rows={5} />
            <FieldDescription>A customer reference, the contract or plan — free text.</FieldDescription>
          </Field>
          {offer ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning px-3 py-2 text-sm" role="alert" data-testid="tenant-record-conflict">
              {record.updatedBy === null ? 'Someone' : record.updatedBy} saved these notes meanwhile.
              <Button type="button" size="sm" variant="outline" onClick={keepMine} data-testid="tenant-record-keep">
                Keep mine
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={loadTheirs} data-testid="tenant-record-reload">
                Discard mine and load theirs
              </Button>
            </div>
          ) : (
            <FieldError>{error}</FieldError>
          )}
          {canEdit ? (
            <Button type="submit" className="w-fit" disabled={!dirty || pending || offer} data-testid="tenant-record-save">
              {pending ? 'Saving…' : 'Save notes'}
            </Button>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
